import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BuildingIcon, ChevronsUpDownIcon, ClipboardListIcon, KanbanSquareIcon, LayoutDashboardIcon, LayoutTemplateIcon, LogOutIcon, MenuIcon, PlugIcon, SettingsIcon, Trash2Icon, UsersIcon, UsersRoundIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBrandTheme } from '../hooks/useBrandTheme';
import { Permission } from '../config/permissions';
import { ROLE_LABELS } from '../types';
import { BrandLogo } from '../components/shared/BrandLogo';
import { SyncStatus } from '../components/shared/SyncStatus';
import { Avatar } from '../components/ui/Avatar';
import { Dropdown } from '../components/ui/Dropdown';
import { Drawer } from '../components/ui/Drawer';
interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  permission?: Permission;
}
const NAV: NavItem[] = [{
  to: '/app/dashboard',
  label: 'Dashboard',
  icon: LayoutDashboardIcon
}, {
  to: '/app/leads',
  label: 'Leads',
  icon: UsersIcon
}, {
  to: '/app/pipeline',
  label: 'Embudo',
  icon: KanbanSquareIcon
}, {
  to: '/app/formularios',
  label: 'Formularios',
  icon: ClipboardListIcon,
  permission: 'forms.manage'
}, {
  to: '/app/landings',
  label: 'Landings',
  icon: LayoutTemplateIcon,
  permission: 'landings.manage'
}, {
  to: '/app/papelera',
  label: 'Papelera',
  icon: Trash2Icon
}, {
  to: '/app/equipo',
  label: 'Equipo',
  icon: UsersRoundIcon,
  permission: 'team.manage'
}, {
  to: '/app/integraciones',
  label: 'Integraciones',
  icon: PlugIcon,
  permission: 'integrations.manage'
}, {
  to: '/app/configuracion',
  label: 'Configuración',
  icon: SettingsIcon,
  permission: 'settings.manage'
}];
function navClass({
  isActive
}: {
  isActive: boolean;
}): string {
  return ['flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px]', isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-surface hover:text-foreground'].join(' ');
}
export function AppLayout() {
  const {
    organization,
    user,
    role,
    hasPermission,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // La identidad visual de la empresa se aplica a toda la plataforma.
  useBrandTheme(organization);
  const items = NAV.filter(item => !item.permission || hasPermission(item.permission));
  async function handleLogout() {
    await logout();
    navigate('/login');
  }
  const nav = <nav className="flex flex-col gap-1" aria-label="Navegación principal" data-id="element-863" data-mp="35e2ygbqbxt9v">
      {items.map(item => <NavLink key={item.to} to={item.to} className={navClass} onClick={() => setMenuOpen(false)} data-id="element-864" data-mp="30ei125qt5sz9">
          <item.icon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-865" data-mp="2vfx0u6bk39at" />
          {item.label}
        </NavLink>)}
    </nav>;
  const workspaceCard = <div className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2.5" data-id="element-866" data-mp="2mgtfswstwjto">
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-primary/10" data-id="element-867" data-mp="11xzdxl9v01cw">
        {organization?.brand.logoUrl ? <img src={organization.brand.logoUrl} alt="" className="h-6 w-6 object-contain" data-id="element-868" data-mp="15yt3ej2y9o7w" /> : <BuildingIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-869" data-mp="1b10gxd3fhccc" />}
      </span>
      <span className="min-w-0 flex-1" data-id="element-870" data-mp="1fxqvtx3hy1w6">
        <span className="block truncate text-sm font-semibold text-foreground" data-id="element-871" data-mp="1fwfwc9h33gav">
          {organization?.name ?? 'Mi empresa'}
        </span>
        <span className="block truncate text-xs text-muted-foreground" data-id="element-872" data-mp="n9d1psfj3ppw">
          {organization?.sector}
        </span>
      </span>
      <ChevronsUpDownIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" data-id="element-873" data-mp="s7myu8ktd0e5" />
    </div>;
  return <div className="flex min-h-screen w-full bg-surface" data-id="element-874" data-mp="x20v0d7avbjv">
      {/* Sidebar escritorio */}
      <aside className="sticky top-0 hidden h-screen w-64 flex-none flex-col border-r border-border bg-background p-4 lg:flex" data-id="element-875" data-mp="3vpoaytxbcxa4">
        <div className="mb-6 px-1" data-id="element-876" data-mp="268sr70snmljg">
          <BrandLogo src={organization?.brand.logoUrl ?? organization?.logoUrl} size={26} alt={organization?.name ?? 'Sistema AVA'} data-id="element-877" data-mp="269ueq4qux0i2" />
        </div>
        <div className="mb-4" data-id="element-878" data-mp="225e8u3h8tt2u">{workspaceCard}</div>
        {nav}
        <div className="mt-auto pt-4" data-id="element-879" data-mp="2fzdxlaekkucx">
          <button type="button" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface hover:text-foreground" data-id="element-880" data-mp="2g0gkl78rucnk">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-881" data-mp="2ky8eu7ww2aux" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Menú móvil */}
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} side="left" title="Menú" data-id="element-882" data-mp="2yvdrwrv0s9zo">
        <div className="space-y-4" data-id="element-883" data-mp="2tz6igp3f0gcz">
          {workspaceCard}
          {nav}
          <button type="button" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-surface" data-id="element-884" data-mp="14k8i15tcb1bw">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-885" data-mp="3b7fxuloucapq" />
            Cerrar sesión
          </button>
        </div>
      </Drawer>

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-886" data-mp="3g60ukb4vwc9f">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:px-6" data-id="element-887" data-mp="32g78u1ooe83x">
          <button type="button" onClick={() => setMenuOpen(true)} aria-label="Abrir menú" className="flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-surface lg:hidden" data-id="element-888" data-mp="36f6c7hurdwt9">
            <MenuIcon className="h-5 w-5" data-id="element-889" data-mp="2xe84aoh7955e" />
          </button>
          <div className="min-w-0 flex-1" data-id="element-890" data-mp="2jhl329i7rsto">
            <p className="truncate text-sm font-semibold text-foreground" data-id="element-891" data-mp="2jiw8edrb58mw">
              {organization?.name}
            </p>
            <p className="truncate text-xs text-muted-foreground" data-id="element-892" data-mp="hon7w70yso5k">
              {role ? ROLE_LABELS[role] : ''}
            </p>
          </div>
          <SyncStatus data-id="element-893" data-mp="lq8h9ieaikb0" />
          <Dropdown align="right" trigger={<span className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-surface" data-id="element-895" data-mp="1uo8a34ntm8ie">
                <Avatar name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`} size="sm" data-id="element-896" data-mp="1po43q7gmmu5w" />
                <span className="hidden text-sm font-medium text-foreground sm:inline" data-id="element-897" data-mp="1lmqvjqq2aevt">
                  {user?.firstName}
                </span>
              </span>} items={[{
          label: 'Configuración de empresa',
          icon: <SettingsIcon className="h-4 w-4" data-id="element-898" data-mp="2dd54d8j7x10m" />,
          onSelect: () => navigate('/app/configuracion')
        }, {
          label: 'Cerrar sesión',
          icon: <LogOutIcon className="h-4 w-4" data-id="element-899" data-mp="29ez48jcvxjoo" />,
          destructive: true,
          onSelect: handleLogout
        }]} data-id="element-894" data-mp="lofjytpkhcfo" />
        </header>

        <main className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:px-10" data-id="element-900" data-mp="1znl4cah0uhv0">
          <Outlet data-id="element-901" data-mp="2x1xev0ebpxeo" />
        </main>
      </div>
    </div>;
}